/**
 * Polls the support worker for human replies while a support conversation is
 * open, appending them to the current stack's chat history as `human`
 * messages. Mounted once in the always-present editor pane; a module-level
 * guard keeps a second mount from double-polling.
 */

import { notify, text } from "@quickpoint.me/shared";
import { useEffect } from "react";
import { useRecoilCallback, useRecoilValue } from "recoil";
import {
	fetchSupportMessages,
	getActiveConversationId,
} from "../lib/support";
import { AiChatHistoryAtom, type ChatMessage } from "../state/aiChatAtoms";
import { StackCfKeyAtom } from "../state/stackAtoms";

const POLL_MS = 15_000;

let pollerMounted = false;
let lastSeenId = 0;

export function useSupportChat() {
	const stackCfKey = useRecoilValue(StackCfKeyAtom);

	const appendHumanMessages = useRecoilCallback(
		({ set }) =>
			(contents: string[]) => {
				if (!stackCfKey) return;
				const now = Date.now();
				const additions: ChatMessage[] = contents.map((content, i) => ({
					id: crypto.randomUUID(),
					role: "human",
					content,
					timestamp: now + i,
				}));
				set(AiChatHistoryAtom(stackCfKey), (prev) => [...prev, ...additions]);
			},
		[stackCfKey],
	);

	useEffect(() => {
		if (pollerMounted) return;
		pollerMounted = true;

		let cancelled = false;

		const tick = async () => {
			if (cancelled) return;
			if (getActiveConversationId()) {
				const result = await fetchSupportMessages(lastSeenId);
				if (result && !cancelled) {
					const fresh = result.messages.filter((m) => m.sender === "human");
					if (fresh.length > 0) {
						appendHumanMessages(fresh.map((m) => m.content));
						notify(text.chat.humanReplied);
					}
					for (const m of result.messages) {
						if (m.id > lastSeenId) lastSeenId = m.id;
					}
				}
			}
			if (!cancelled) setTimeout(tick, POLL_MS);
		};

		void tick();

		return () => {
			cancelled = true;
			pollerMounted = false;
		};
	}, [appendHumanMessages]);
}
